import { Button } from '@/app/common/components/button/Button'
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@/common/components/Card.tsx'
import Pagination from '@/common/components/Pagination.tsx'
import SearchInput from '@/common/components/SearchInput.tsx'
import ApplicationStatusBadge from '@/common/components/StatusBadge.tsx'
import { CircleArrowsIcon } from '@/common/icons/arrows.tsx'
import { cn } from '@/common/utilities.ts'
import { useAdminTransactions } from '@/pages/providers/hooks/useAdminTransactions.ts'
import { RootState } from '@/storage/store.ts'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { format, parseISO } from 'date-fns'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import AdminAuthGuard from '../admin-auth-guard'

export type TransactionStatus = 'Approved' | 'Declined' | 'In Review'
export type IProviderTransaction = {
  id: number
  applicant_id: number
  student_id: number
  transaction_no: string
  applicant_name: string
  student_name: string
  category: string
  status: TransactionStatus
  submitted_at: string
}
const statusMap: Record<number, TransactionStatus> = {
  1: 'Approved',
  2: 'Declined',
  3: 'In Review',
}

const columnHelper = createColumnHelper<IProviderTransaction>()
const RECORDS_PER_PAGE = 10 as const

export default function ProviderTransactionsTab() {
  const [currentPage, setCurrentPage] = useState(1)
  const [search, setSearch] = useState('')
  const [recordsPerPage, setRecordsPerPage] = useState<number>(RECORDS_PER_PAGE)
  const navigate = useNavigate()

  const { selectedProgramYear } = useSelector((s: RootState) => s.programs)

  const { providerId } = useParams()

  const { data } = useAdminTransactions(
    selectedProgramYear?.id ?? 0,
    recordsPerPage,
    1,
    'en',
    { provider_id: providerId ? parseInt(providerId, 10) : 0 }
  )

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function mapApiDataToProviderTransaction(apiData: any): IProviderTransaction {
    return {
      id: apiData.transaction_id,
      applicant_id: apiData.applicant_id,
      student_id: 0,
      transaction_no: String(apiData.transaction_id),
      applicant_name: apiData.applicant_name,
      student_name: apiData.student_name?.[0] ?? '',
      category: apiData.transaction_type ?? 'N/A',
      status: (statusMap[apiData.status] ?? 'Approved') as TransactionStatus,
      submitted_at: apiData.created_datetime,
    }
  }
  const tableData = data?.data.map(mapApiDataToProviderTransaction) ?? []

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handlePerPageChange = (perPage: number) => {
    setRecordsPerPage(perPage)
  }

  const columns = [
    columnHelper.accessor('transaction_no', {
      id: 'transaction_no',
      header: () => (
        <>
          <span className="hidden pl-4 sm:block">{'Transaction No.'}</span>
          <span className="block pl-4 sm:hidden">{'Transaction'}</span>
        </>
      ),
      cell: (info) => {
        const row = info.row.original
        return (
          <div className="flex items-center">
            <div className="w-3/4 sm:w-full font-bold pl-4">
              <div className="font-medium text-gray-900 flex justify-between items-center">
                <span>{row.transaction_no}</span>
                <ApplicationStatusBadge
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  status={row.status as any}
                  className="md:hidden"
                />
              </div>
              <div className="mt-2 space-y-1 text-gray-500 font-normal md:hidden">
                <div>{row.student_name}</div>
                <div>{row.category}</div>
              </div>
            </div>
          </div>
        )
      },
    }),
    columnHelper.accessor('applicant_name', {
      id: 'applicant_name',
      header: () => 'Applicant Name',
      cell: (info) => <div>{info.getValue()}</div>,
    }),
    columnHelper.accessor('student_name', {
      id: 'student_name',
      header: () => 'Student Name',
      cell: (info) => <div>{info.getValue()}</div>,
    }),
    columnHelper.accessor('category', {
      id: 'category',
      header: () => 'Category',
      cell: (info) => <div>{info.getValue()}</div>,
    }),
    columnHelper.accessor('status', {
      id: 'status',
      header: () => 'Status',
      cell: (info) => (
        <div>
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          <ApplicationStatusBadge status={info.getValue() as any} />
        </div>
      ),
    }),
    columnHelper.accessor('submitted_at', {
      header: () => 'Submitted At',
      cell: (info) => {
        const lastDateTimeStr = info.getValue()
        return (
          <span>
            {lastDateTimeStr !== null
              ? format(parseISO(lastDateTimeStr), 'MM/dd/yyyy, h:mm aa')
              : ''}
          </span>
        )
      },
    }),
    columnHelper.display({
      id: 'action',
      header: () => <span className="pr-2 2xl:p-1">Action</span>,
      cell: (info) => {
        const row = info.row.original
        return (
          <AdminAuthGuard requiredPermissions={['provider_management_edit']}>
            <Button
              onClick={() => {
                navigate(`/transactions/${row.id}/details`)
              }}
            >
              Review
            </Button>
          </AdminAuthGuard>
        )
      },
    }),
  ]

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: 1,
  })

  return (
    <>
      <section aria-labelledby="provider-transactions-list">
        <Card className="mt-6">
          <CardHeader className="flex justify-between items-center text-md font-semibold">
            <div className="text-left text-lg md:text-md text-gray-900">
              Transactions
              <span className="hidden mt-1 w-15ch text-sm font-normal text-gray-400 lg-sm:block lg-sm:w-full">
                {'last updated at: 06:05:32 PM'}
              </span>
            </div>
            <div>
              <div className="space-y-3 flex flex-col items-start lg-sm:flex-row lg-sm:items-center lg-sm:space-y-0 lg-sm:space-x-4">
                <SearchInput
                  id="provider_transactions_search"
                  placeholder="Search Transactions"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <div className="flex space-x-2 lg-sm:flex-col lg-sm:space-x-0">
                  <Button
                    variant="outline"
                    className="max-w-12"
                    size="md"
                    disabled={false}
                    onClick={() => {}}
                  >
                    <CircleArrowsIcon className="text-base" />
                  </Button>
                  <span className="block w-15ch text-sm font-normal text-gray-400 lg-sm:w-full lg-sm:hidden">
                    {'last updated at: 06:05:32 PM'}
                  </span>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="flex flex-col p-0 lg:p-0 h-full w-full">
            <table className="w-full">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header, index) => (
                      <th
                        key={header.id}
                        className={cn(
                          'w-[12%] px-2 py-3.5 text-left text-sm font-semibold text-gray-900 md:py-4 md:px-2.5',
                          index != 0 &&
                            index != headerGroup.headers.length - 1 &&
                            'hidden md:table-cell',
                          index == headerGroup.headers.length - 1 && 'text-left'
                        )}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.length > 0 ? (
                  table.getRowModel().rows.map((row) => (
                    <tr key={row.id}>
                      {row.getVisibleCells().map((cell, index) => (
                        <td
                          key={cell.id}
                          className={cn(
                            'w-[12%] py-4 px-2 text-sm font-sans text-left text-gray-500 border-t border-gray-200 md:py-4 md:px-2',
                            index != 0 &&
                              index != row.getVisibleCells().length - 1 &&
                              'hidden md:table-cell',
                            index == row.getVisibleCells().length - 1 &&
                              'text-left'
                          )}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={columns.length}
                      className="px-4 py-4 text-center text-sm text-gray-500 border-t"
                    >
                      No transactions found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </CardContent>
          <CardFooter className="p-0 border-t border-transparent mt-5">
            <Pagination
              itemsPerPage={recordsPerPage}
              currentPage={currentPage}
              totalRows={data?.total ?? 0}
              onPageChange={handlePageChange}
              onPerPageChange={handlePerPageChange}
            />
          </CardFooter>
        </Card>
      </section>
    </>
  )
}
